import { memo, useCallback, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    ButtonAtom,
    DatePickerAtom,
    TextAreaAtom,
    TextFieldAtom
} from "../../../../components/atoms/atom";
import INVOICE_STATUS from "../../../../constants/invoice-status";

const RejectedInvoiceForm = memo(({
    ticketCategory,
    onSubmit,
    isSubmitting = false,
}) => {
    const { t } = useTranslation();

    // Form state - aligned with V1 structure
    const [formData, setFormData] = useState({
        invoiceNumber: "",
        invoiceDate: new Date(),
        billingPeriodFrom: new Date(),
        billingPeriodTo: new Date(),
        rejectionReason: "",
    });

    const [errors, setErrors] = useState({});

    // Enhanced form field update handler with V1 validation logic
    const handleFieldChange = useCallback((field, value) => {
        // Update the form data
        setFormData(prevData => ({
            ...prevData,
            [field]: value,
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [field]: null,
            }));
        }

        // Handle validation for billing period dates - same logic as V1
        if (field === "billingPeriodFrom" || field === "billingPeriodTo") {
            // Convert dates to date-only objects for accurate comparison
            const fromDateOnly = formData.billingPeriodFrom && new Date(formData.billingPeriodFrom.toDateString());
            const toDateOnly = formData.billingPeriodTo && new Date(formData.billingPeriodTo.toDateString());
            const valueAsDateOnly = value && new Date(value.toDateString());

            // Validate that billingPeriodTo is not before billingPeriodFrom
            if (field === "billingPeriodTo" && fromDateOnly && valueAsDateOnly < fromDateOnly) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    billingPeriodTo: "invoice.error-billing-period-to-less-than-from"
                }));
                return;
            }

            // Validate that billingPeriodFrom is not after billingPeriodTo
            if (field === "billingPeriodFrom" && toDateOnly && valueAsDateOnly > toDateOnly) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    billingPeriodFrom: "invoice.error-billing-period-from-after-to"
                }));
                return;
            }
        }
    }, [formData, errors]);

    // Enhanced validation - same comprehensive logic as V1
    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!formData.invoiceNumber.trim()) {
            newErrors.invoiceNumber = "invoice.error-invoice-number-required";
        }

        if (!formData.invoiceDate) {
            newErrors.invoiceDate = "invoice.error-invoice-date-required";
        }

        if (!formData.billingPeriodFrom) {
            newErrors.billingPeriodFrom = "invoice.error-billing-period-from-required";
        }

        if (!formData.billingPeriodTo) {
            newErrors.billingPeriodTo = "invoice.error-billing-period-to-required";
        }

        // Additional validation that invoice date cannot be before billing period from
        if (
            formData.invoiceDate &&
            formData.billingPeriodFrom &&
            new Date(formData.invoiceDate.toDateString()) < new Date(formData.billingPeriodFrom.toDateString())
        ) {
            newErrors.invoiceDate = "invoice.error-invoice-date-greater-than-billing-period-from";
        }

        // Additional validation that invoice date cannot be after billing period to
        if (
            formData.invoiceDate &&
            formData.billingPeriodTo &&
            new Date(formData.invoiceDate.toDateString()) < new Date(formData.billingPeriodTo.toDateString())
        ) {
            newErrors.invoiceDate = "invoice.error-invoice-date-greater-than-billing-period-to";
        }

        if (!formData.rejectionReason.trim()) {
            newErrors.rejectionReason = "invoice.error-rejection-reason-required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    // Enhanced submit handler using proper INVOICE_STATUS constant
    const handleSubmit = useCallback((e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Prepare the invoice data for submission - aligned with V1 structure
        const invoiceData = {
            invoiceNumber: formData.invoiceNumber,
            invoiceDate: formData.invoiceDate,
            billingPeriodFrom: formData.billingPeriodFrom,
            billingPeriodTo: formData.billingPeriodTo,
            projectTicketCategoryId: ticketCategory.value,
            status: INVOICE_STATUS.REJECTED,
            rejectionReason: formData.rejectionReason,
            salesTaxPercentage: 0,
            positions: [], // Rejected invoices don't have positions
        };

        // Submit to parent component for API call
        onSubmit(invoiceData);
    }, [formData, ticketCategory, validateForm, onSubmit]);

    // Date constraints - same logic as V1
    const maxDate = formData.invoiceDate || undefined;
    const minDateTo = formData.billingPeriodFrom || undefined;
    const maxDateFrom = formData.billingPeriodTo || undefined;

    return (
        <form onSubmit={handleSubmit}>
            <Card className="mb-4">
                <Card.Header className="bg-danger text-white">
                    <h5 className="mb-0">{t('invoice.rejected-invoice-form-title')}</h5>
                </Card.Header>
                <Card.Body>
                    {/* Invoice Basic Details */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <TextFieldAtom
                                type="text"
                                label={t("invoice.invoice-number")}
                                placeholder={t("invoice.invoice-number")}
                                required
                                name="invoiceNumber"
                                value={formData.invoiceNumber}
                                error={errors.invoiceNumber ? t(errors.invoiceNumber) : ""}
                                onChange={(e) => handleFieldChange("invoiceNumber", e.target.value)}
                                disabled={isSubmitting}
                            />
                        </Col>
                        <Col md={6}>
                            <DatePickerAtom
                                label={t("invoice.invoice-date")}
                                placeholderText={t("invoice.invoice-date")}
                                selected={formData.invoiceDate}
                                onChange={(date) => handleFieldChange("invoiceDate", date)}
                                error={errors.invoiceDate ? t(errors.invoiceDate) : ""}
                                required
                                disabled={isSubmitting}
                            />
                        </Col>
                    </Row>

                    {/* Billing Period */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <DatePickerAtom
                                label={t("invoice.billing-period-from")}
                                placeholderText={t("invoice.billing-period-from")}
                                selected={formData.billingPeriodFrom}
                                onChange={(date) => handleFieldChange("billingPeriodFrom", date)}
                                error={errors.billingPeriodFrom ? t(errors.billingPeriodFrom) : ""}
                                required
                                disabled={isSubmitting}
                                maxDate={
                                    maxDate && maxDateFrom
                                        ? maxDate < maxDateFrom
                                            ? maxDate
                                            : maxDateFrom
                                        : maxDate || maxDateFrom
                                }
                            />
                        </Col>
                        <Col md={6}>
                            <DatePickerAtom
                                label={t("invoice.billing-period-to")}
                                placeholderText={t("invoice.billing-period-to")}
                                selected={formData.billingPeriodTo}
                                onChange={(date) => handleFieldChange("billingPeriodTo", date)}
                                error={errors.billingPeriodTo ? t(errors.billingPeriodTo) : ""}
                                required
                                disabled={isSubmitting}
                                maxDate={maxDate}
                                minDate={minDateTo}
                            />
                        </Col>
                    </Row>

                    {/* Rejection Reason */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <TextAreaAtom
                                label={t('invoice.rejection-reason')}
                                placeholder={t('invoice.rejection-reason')}
                                name="rejectionReason"
                                value={formData.rejectionReason}
                                onChange={(e) => handleFieldChange("rejectionReason", e.target.value)}
                                error={errors.rejectionReason ? t(errors.rejectionReason) : ""}
                                required={true}
                                rows={3}
                                disabled={isSubmitting}
                            />
                        </Col>
                    </Row>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end mt-3">
                        <ButtonAtom
                            className="btn btn-secondary me-2"
                            onClick={() => window.history.back()}
                            type="button"
                            disabled={isSubmitting}
                        >
                            {t('common.cancel')}
                        </ButtonAtom>
                        <ButtonAtom
                            className="btn btn-danger"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? t('common.saving') : t('invoice.save-rejected-invoice')}
                        </ButtonAtom>
                    </div>
                </Card.Body>
            </Card>
        </form>
    );
});

RejectedInvoiceForm.displayName = "RejectedInvoiceForm";

export default RejectedInvoiceForm;
